import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, FileText, Heart, Clock, MapPin, User, Plus, Pencil, Trash2, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import api, { formatApiError } from "../lib/api";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import RecentlyViewed from "../components/RecentlyViewed";
import { ReturnDialog, ModDialog } from "../components/OrderRequestDialogs";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const CANCEL_REASONS = ["Ordered by mistake", "Found a better price elsewhere", "Delivery time is too long", "Want to change my order", "Change of mind", "Other"];
const RETURN_REASONS = ["Item arrived damaged", "Item is defective", "Wrong item delivered", "Item not as described", "Other"];
const MOD_TYPES = [["address", "Correct delivery address"], ["quantity", "Change quantity"], ["product", "Change a product"], ["other", "Something else"]];
const TABS = [
  ["orders", "My Orders", Package], ["requests", "My Requests", FileText], ["wishlist", "Wishlist", Heart],
  ["recent", "Recently Viewed", Clock], ["address", "Address", MapPin], ["profile", "Profile", User],
];
const STATUS_CLS = {
  Delivered: "bg-green-100 text-green-900", Cancelled: "bg-red-100 text-red-900",
  pending: "bg-gold-light text-[#111]", approved: "bg-green-100 text-green-900", rejected: "bg-red-100 text-red-900",
  received: "bg-blue-100 text-blue-900", processed: "bg-green-100 text-green-900", resolved: "bg-green-100 text-green-900",
};

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const { wishlist } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [modTarget, setModTarget] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrEditing, setAddrEditing] = useState(null);
  const [addrForm, setAddrForm] = useState({ line1: "", line2: "", city: "", state: "", pincode: "", default: false });
  const addresses = user?.addresses || [];
  const requestOrders = new Set(requests.map((r) => r.order_number));
  const filteredOrders = orders.filter((o) => {
    if (orderFilter === "all") return true;
    if (orderFilter === "processing") return ["Placed", "Confirmed", "Processing"].includes(o.status);
    if (orderFilter === "returns") return requestOrders.has(o.order_number);
    return o.status.toLowerCase() === orderFilter;
  });

  const saveAddresses = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { addresses: next });
      setUser(data);
      toast.success("Addresses updated");
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAddr = async (e) => {
    e.preventDefault();
    let next = [...addresses];
    if (addrForm.default) next = next.map((a) => ({ ...a, default: false }));
    const entry = { ...addrForm, default: addrForm.default || next.length === 0 };
    if (addrEditing === null) next.push(entry);
    else next[addrEditing] = entry;
    if (await saveAddresses(next)) setAddrOpen(false);
  };

  const deleteAddr = async (idx) => {
    const next = addresses.filter((_, i) => i !== idx);
    if (next.length && !next.some((a) => a.default)) next[0] = { ...next[0], default: true };
    await saveAddresses(next);
  };

  const setDefaultAddr = async (idx) => {
    await saveAddresses(addresses.map((a, i) => ({ ...a, default: i === idx })));
  };

  const loadOrders = () => api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
  const loadRequests = () => api.get("/requests/my").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    if (user === false) navigate("/login?redirect=/account");
    if (user) {
      setForm({ name: user.name, phone: user.phone || "" });
      loadOrders();
      loadRequests();
    }
  }, [user, navigate]);

  if (!user) return null;

  const confirmCancel = async () => {
    if (!cancelReason) return toast.error("Please select a cancellation reason");
    setCancelling(true);
    try {
      await api.post(`/orders/${cancelTarget.order_number}/cancel`, { reason: cancelReason });
      toast.success(`Order ${cancelTarget.order_number} cancelled`);
      setCancelTarget(null);
      setCancelReason("");
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="account-page">
      <SEO title="My Account" description="Your Humaira Crafts account" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="text-4xl tracking-tighter font-semibold">Hello, {user.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-destructive transition-colors duration-300" data-testid="account-logout">
          <LogOut strokeWidth={1.5} size={15} /> Logout
        </button>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-black/10 mb-10 -mx-4 px-4 md:mx-0 md:px-0" data-testid="account-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap border-b-2 transition-colors duration-300 ${tab === id ? "border-gold text-gold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-${id}`}>
            <Icon strokeWidth={1.5} size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        orders.length === 0 ? (
          <div className="text-center py-16 bg-white border border-black/10" data-testid="no-orders">
            <p className="font-serif text-xl">No orders yet</p>
            <Link to="/shop" className="small-caps text-gold border-b border-gold mt-3 inline-block" data-testid="account-shop-link">Start Shopping</Link>
          </div>
        ) : (
          <>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="order-filters">
            {[["all", "All"], ["processing", "Processing"], ["shipped", "Shipped"], ["delivered", "Delivered"], ["cancelled", "Cancelled"], ["returns", "Returns"]].map(([id, label]) => (
              <button key={id} onClick={() => setOrderFilter(id)}
                className={`px-4 py-1.5 text-[11px] uppercase tracking-widest border transition-colors duration-300 ${orderFilter === id ? "bg-[#111] text-[#FAF9F6] border-[#111]" : "border-black/15 hover:border-gold hover:text-gold"}`}
                data-testid={`order-filter-${id}`}>
                {label}
              </button>
            ))}
          </div>
          {filteredOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-filtered-orders">No orders in this view.</p>
          ) : (
          <div className="flex flex-col gap-4" data-testid="orders-list">
            {filteredOrders.map((o) => (
              <div key={o.id} className="bg-white border border-black/10 p-6" data-testid={`order-${o.order_number}`}>
                <div className="flex flex-wrap justify-between gap-2 items-baseline">
                  <p className="font-serif text-lg">{o.order_number}</p>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_CLS[o.status] || "bg-gold-light text-[#111]"}`}>{o.status}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {o.payment_method === "cod" ? "COD" : "Paid Online"}
                  {o.payment_status && o.payment_status !== "cod" ? ` · ${o.payment_status}` : ""}
                </p>
                <div className="text-sm text-muted-foreground mt-3">
                  {o.items.map((i) => <p key={i.product_id}>{i.name} × {i.qty}</p>)}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                  <p className="text-sm font-semibold">₹{o.total.toLocaleString("en-IN")}</p>
                  <div className="flex flex-wrap gap-4">
                    <Link to={`/track-order?order=${o.order_number}&email=${encodeURIComponent(user.email)}`} className="text-xs uppercase tracking-widest text-gold border-b border-gold hover:opacity-70 transition-opacity duration-300" data-testid={`track-${o.order_number}`}>
                      Track Order
                    </Link>
                    {["Placed", "Confirmed", "Processing"].includes(o.status) && (
                      <button onClick={() => setModTarget(o)} className="text-xs uppercase tracking-widest border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`modify-order-${o.order_number}`}>
                        Request Change
                      </button>
                    )}
                    {o.status === "Delivered" && (
                      <button onClick={() => setReturnTarget(o)} className="text-xs uppercase tracking-widest border-b border-black hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`return-order-${o.order_number}`}>
                        Return / Refund / Exchange
                      </button>
                    )}
                    {["Placed", "Confirmed"].includes(o.status) && (
                      <button onClick={() => setCancelTarget(o)} className="text-xs uppercase tracking-widest text-destructive border-b border-destructive hover:opacity-70 transition-opacity duration-300" data-testid={`cancel-order-${o.order_number}`}>
                        Cancel Order
                      </button>
                    )}
                  </div>
                </div>
                {o.status === "Cancelled" && o.cancellation_reason && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid={`cancel-reason-${o.order_number}`}>
                    Cancellation reason: {o.cancellation_reason}{o.refund_status === "pending" ? " · Refund pending" : ""}
                  </p>
                )}
              </div>
            ))}
          </div>
          )}
          </>
        )
      )}

      {tab === "requests" && (
        requests.length === 0 ? (
          <div className="text-center py-16 bg-white border border-black/10" data-testid="no-requests">
            <p className="font-serif text-xl">No requests yet</p>
            <p className="text-sm text-muted-foreground mt-2">Return, exchange and order-change requests will appear here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="requests-list">
            {requests.map((r) => (
              <div key={r.id} className="bg-white border border-black/10 p-6" data-testid={`request-${r.id}`}>
                <div className="flex flex-wrap justify-between gap-2 items-baseline">
                  <p className="font-serif text-lg">
                    {r.kind === "return" ? r.type : "Order Change"} — {r.order_number}
                  </p>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${STATUS_CLS[r.status] || "bg-gold-light text-[#111]"}`}>{r.status}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
                <p className="text-sm text-muted-foreground mt-3">{r.kind === "return" ? `Reason: ${r.reason}` : r.message}</p>
                {r.items && <p className="text-xs text-muted-foreground mt-1">{r.items.map((i) => `${i.name} × ${i.qty}`).join(", ")}</p>}
                {r.refund_status && <p className="text-xs mt-2">Refund status: <span className="font-medium">{r.refund_status}</span></p>}
                {r.exchange_status && <p className="text-xs mt-1">Exchange status: <span className="font-medium">{r.exchange_status}</span></p>}
                {r.admin_notes && <p className="text-xs mt-2 bg-muted/50 p-3">Note from our team: {r.admin_notes}</p>}
              </div>
            ))}
          </div>
        )
      )}

      {tab === "wishlist" && (
        wishlist.length === 0 ? (
          <div className="text-center py-16 bg-white border border-black/10" data-testid="no-wishlist">
            <p className="font-serif text-xl">Nothing saved yet</p>
            <Link to="/shop" className="small-caps text-gold border-b border-gold mt-3 inline-block" data-testid="account-wishlist-shop">Explore the Collection</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="account-wishlist-grid">
            {wishlist.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )
      )}

      {tab === "recent" && <RecentlyViewed title="Recently Viewed" />}

      {tab === "address" && (
        <div className="max-w-2xl" data-testid="address-tab">
          <div className="flex items-center justify-between mb-6">
            <p className="small-caps text-gold">Saved Addresses</p>
            <button onClick={() => { setAddrEditing(null); setAddrForm({ line1: "", line2: "", city: "", state: "", pincode: "", default: addresses.length === 0 }); setAddrOpen(true); }}
              className="flex items-center gap-2 border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300" data-testid="address-add">
              <Plus strokeWidth={1.5} size={14} /> Add Address
            </button>
          </div>
          {addresses.length === 0 ? (
            <div className="text-center py-16 bg-white border border-black/10" data-testid="no-addresses">
              <p className="font-serif text-xl">No saved addresses</p>
              <p className="text-sm text-muted-foreground mt-2">Save an address once and it pre-fills at checkout.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3" data-testid="address-list">
              {addresses.map((a, i) => (
                <div key={i} className="bg-white border border-black/10 p-5 flex flex-wrap items-center gap-3" data-testid={`address-${i}`}>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm">
                      {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}
                      {a.default && <span className="ml-2 text-[10px] uppercase tracking-widest text-gold border border-gold px-2 py-0.5">Default</span>}
                    </p>
                  </div>
                  {!a.default && (
                    <button onClick={() => setDefaultAddr(i)} className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-gold transition-colors duration-300 flex items-center gap-1" data-testid={`address-default-${i}`}>
                      <Star strokeWidth={1.5} size={13} /> Set Default
                    </button>
                  )}
                  <button onClick={() => { setAddrEditing(i); setAddrForm({ line1: a.line1, line2: a.line2 || "", city: a.city, state: a.state, pincode: a.pincode, default: !!a.default }); setAddrOpen(true); }} aria-label="Edit address" className="hover:text-gold transition-colors duration-300" data-testid={`address-edit-${i}`}>
                    <Pencil strokeWidth={1.5} size={14} />
                  </button>
                  <button onClick={() => deleteAddr(i)} aria-label="Delete address" className="hover:text-destructive transition-colors duration-300" data-testid={`address-delete-${i}`}>
                    <Trash2 strokeWidth={1.5} size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
            <DialogContent className="bg-[#FAF9F6] max-w-md" data-testid="address-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{addrEditing === null ? "Add Address" : "Edit Address"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={saveAddr} className="grid grid-cols-2 gap-3 mt-2">
                <Input placeholder="Address Line 1 *" required value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} className="col-span-2 bg-white" data-testid="addr-line1" />
                <Input placeholder="Address Line 2" value={addrForm.line2} onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })} className="col-span-2 bg-white" data-testid="addr-line2" />
                <Input placeholder="City *" required value={addrForm.city} onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })} className="bg-white" data-testid="addr-city" />
                <Input placeholder="State *" required value={addrForm.state} onChange={(e) => setAddrForm({ ...addrForm, state: e.target.value })} className="bg-white" data-testid="addr-state" />
                <Input placeholder="Pincode *" required value={addrForm.pincode} onChange={(e) => setAddrForm({ ...addrForm, pincode: e.target.value })} className="bg-white" data-testid="addr-pincode" />
                <label className="flex items-center gap-2 text-sm col-span-2" data-testid="addr-default-row">
                  <input type="checkbox" checked={addrForm.default} onChange={(e) => setAddrForm({ ...addrForm, default: e.target.checked })} data-testid="addr-default" /> Set as default address
                </label>
                <button type="submit" disabled={saving} className="col-span-2 bg-[#111] text-[#FAF9F6] py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="addr-save">
                  {saving ? "Saving..." : "Save Address"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === "profile" && (
        <div className="bg-white border border-black/10 p-8 max-w-xl" data-testid="profile-tab">
          <p className="small-caps text-gold mb-6">Profile</p>
          <form onSubmit={saveProfile} className="flex flex-col gap-4">
            <Input placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
            <button type="submit" disabled={saving} className="bg-[#111] text-[#FAF9F6] py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="profile-save">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <DialogContent className="bg-[#FAF9F6] max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cancel Order {cancelTarget?.order_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">This cannot be undone. Stock will be released and any online payment will be refunded.</p>
          <Select value={cancelReason} onValueChange={setCancelReason}>
            <SelectTrigger className="rounded-sm bg-white" data-testid="cancel-reason-select"><SelectValue placeholder="Select a reason *" /></SelectTrigger>
            <SelectContent>
              {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-3 mt-2">
            <button onClick={confirmCancel} disabled={cancelling} className="flex-1 bg-destructive text-white py-3 small-caps hover:opacity-90 transition-opacity duration-300 disabled:opacity-50" data-testid="confirm-cancel-button">
              {cancelling ? "Cancelling..." : "Confirm Cancellation"}
            </button>
            <button onClick={() => setCancelTarget(null)} className="flex-1 border border-black/20 py-3 small-caps hover:border-gold transition-colors duration-300" data-testid="keep-order-button">
              Keep Order
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {returnTarget && (
        <ReturnDialog order={returnTarget} email={user.email} onClose={() => setReturnTarget(null)} onDone={() => { setReturnTarget(null); loadRequests(); setTab("requests"); }} />
      )}
      {modTarget && (
        <ModDialog order={modTarget} email={user.email} onClose={() => setModTarget(null)} onDone={() => { setModTarget(null); loadRequests(); setTab("requests"); }} />
      )}
    </div>
  );
}

